import { type FormEvent, useEffect, useState } from "react";
import { Button, Input, Modal } from "@legal-500/legal500-design-system";
import { powerlistEditions, type Edition, type Series } from "../../api/powerlist.service";
import { FormError, errorMessage } from "./shared";
import styles from "./Powerlist.module.css";

type EditionFormModalProps = {
  isOpen: boolean;
  series: Series;
  edition: Edition | null;
  onClose: () => void;
  onSaved: (edition: Edition) => void;
};

export function EditionFormModal({
  isOpen,
  series,
  edition,
  onClose,
  onSaved,
}: EditionFormModalProps) {
  const [year, setYear] = useState("");
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setYear(String(edition?.year ?? new Date().getFullYear()));
    setTitle(edition?.title ?? "");
    setSlug(edition?.slug ?? "");
    setError(null);
    setSaving(false);
  }, [isOpen, edition]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!/^\d{4}$/.test(year.trim())) {
      setError("Year must be a four-digit year");
      return;
    }
    setSaving(true);
    setError(null);
    const fields = {
      year: Number(year),
      title: title.trim() || undefined,
      slug: slug.trim() || undefined,
    };
    try {
      onSaved(
        edition
          ? await powerlistEditions.update(edition.id, fields)
          : await powerlistEditions.create({ seriesId: series.id, ...fields }),
      );
    } catch (saveError) {
      setError(errorMessage(saveError));
      setSaving(false);
    }
  }

  const defaultSlug = `${series.slug}-${year.trim() || "year"}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={edition ? "Edit edition" : "New edition"}>
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <FormError message={error} />
        <Input
          id="powerlist-edition-year"
          label="Year"
          type="number"
          inputMode="numeric"
          value={year}
          onChange={event => setYear(event.target.value)}
          required
          fullWidth
        />
        <Input
          id="powerlist-edition-title"
          label="Title"
          value={title}
          onChange={event => setTitle(event.target.value)}
          helperText={edition ? undefined : `Leave blank for "${series.name} ${year.trim()}"`}
          fullWidth
        />
        <Input
          id="powerlist-edition-slug"
          label="Slug"
          value={slug}
          onChange={event => setSlug(event.target.value)}
          helperText={edition ? undefined : `Leave blank for ${defaultSlug}`}
          fullWidth
        />
        <div className={styles.formActions}>
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={saving}>
            {saving ? "Saving..." : edition ? "Save" : "Create edition"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
